<template>
  <phone-frame bordered :style="brandProperties">
    <div v-if="markup" class="app-preview" v-html="markup" />
    <v-layout v-else fill-height align-center justify-center class="app-preview__status">
      <span v-if="failed">{{ adminText('workflowsAll.preview_unavailable_36d31d4', 'Preview unavailable') }}</span>
      <spinner v-else />
    </v-layout>
  </phone-frame>
</template>

<script>
import WorkflowDisplay from '@/admin-i18n/workflows-all-display'
import PhoneFrame from './phone-frame'
import { DEFAULT_HUE, getBrandPalette, hexToHue } from '@/utils/color'

/*
  COR-1684: a conference app screen repainted in the organizer's brand colour.

  The screens are static Figma SVG exports in `public/app-previews/`, with every brand
  paint replaced by the custom properties set below. To refresh one, export it from Figma
  again and swap its brand fills for the `--app-preview-brand*` properties by hand. They have to be inlined
  rather than shown through <img>, which would not let the page's properties reach them.
  The markup is our own static asset, served from the same origin.
*/

// Shared across instances: each file is fetched once per session, however often the
// organizer flips between pages and themes.
const requests = {}

const loadScreen = file => {
  if (!requests[file]) {
    requests[file] = fetch(`${process.env.BASE_URL}app-previews/${file}`).then(response => {
      if (!response.ok) throw new Error(`${file}: ${response.status}`)

      return response.text()
    })
    requests[file].catch(() => delete requests[file])
  }

  return requests[file]
}

export default {
  mixins: [WorkflowDisplay],
  name: 'app-preview',
  components: { PhoneFrame },
  props: {
    // One of the pages the build script exports: home, agenda, people, chat, more.
    page: {
      type: String,
      required: true,
    },
    dark: {
      type: Boolean,
      default: false,
    },
    primaryColor: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      markup: null,
      failed: false,
    }
  },
  computed: {
    file() {
      return `${this.page}-${this.dark ? 'dark' : 'light'}.svg`
    },
    brandProperties() {
      const hue = hexToHue(this.primaryColor)
      // The app's Accent / 40, / 20 and / 10 - see getBrandPalette.
      const [brand, tint20, tint10] = getBrandPalette(hue === null ? DEFAULT_HUE : hue)

      return {
        '--app-preview-brand': brand,
        '--app-preview-brand-tint-20': tint20,
        '--app-preview-brand-tint-10': tint10,
      }
    },
  },
  watch: {
    file: {
      immediate: true,
      handler(file) {
        this.markup = null
        this.failed = false
        loadScreen(file).then(
          markup => {
            // A slower earlier request must not overwrite the screen picked after it.
            if (file === this.file) this.markup = markup
          },
          () => {
            if (file === this.file) this.failed = true
          },
        )
      },
    },
  },
}
</script>

<style scoped>
.app-preview >>> svg {
  display: block;
}

.app-preview__status {
  background-color: #f5f5f5;
  font-family: 'Source Sans Pro', sans-serif;
  color: #979797;
}
</style>
